import {useCallback, useReducer} from 'react';

import {getState, callOnChangeProps, getDefaultValue} from './utils';

import * as stateChangeTypes from './stateChangeTypes';

/** UTILS */
function getNextWrappingIndex({moveAmount, baseIndex, itemsLength, circular = true}) {
  if (baseIndex === -1) {
    return moveAmount > 0 ? 0 : itemsLength - 1;
  }
  const nextIndex = baseIndex + moveAmount;

  if (nextIndex < 0) {
    return circular ? itemsLength - 1 : 0;
  }
  if (nextIndex >= itemsLength) {
    return circular ? 0 : itemsLength - 1;
  }

  return nextIndex;
}
/** UTILS END */
export default function autosuggestReducer(state, action) {
  // actions

  // reset, ✅
  // openMenu, ✅
  // closeMenu, ✅
  // toggleMenu, ✅
  // selectItem, ✅
  // selectItemAtIndex,
  // selectHighlightedItem,
  // setHighlightedIndex,  ✅
  // clearSelection, ✅
  // clearItems,
  // setItemCount, // not needed for our easy usecase
  // unsetItemCount, // not needed for our easy usecase

  const {type, props} = action;

  let changes;

  function getChangesAfterReset() {
    const selectedItem = state.selectedItem
      ? state.selectedItem
      : getDefaultValue(props, 'selectedItem');

    const inputValue = state.selectedItem
      ? props.itemToString(state.selectedItem)
      : getDefaultValue(props, 'inputValue');
    return {
      highlightedIndex: getDefaultValue(props, 'highlightedIndex'),
      isOpen: getDefaultValue(props, 'isOpen'),
      selectedItem,
      inputValue,
    };
  }

  function getChangesAfterItemSelection(toSelectItem = action.toSelectItem) {
    return {
      isOpen: getDefaultValue(props, 'isOpen'),
      highlightedIndex: getDefaultValue(props, 'highlightedIndex'),
      selectedItem: toSelectItem,
      inputValue: props.itemToString(action.selectedItem),
    };
  }

  switch (type) {
    // Changes Triggered by Input Actions
    case stateChangeTypes.onInitialValueChange:
      changes = {
        highlightedIndex: getDefaultValue(props, 'highlightedIndex'),
        inputValue: action.inputValue,
      }
    break;
    case stateChangeTypes.InputChange:
      changes = {
        isOpen: true,
        highlightedIndex: getDefaultValue(props, 'highlightedIndex'),
        inputValue: action.inputValue,
      };
      break;

    case stateChangeTypes.InputBlur:
    case stateChangeTypes.OutsideAutosuggestClick:
    case stateChangeTypes.Reset:
      changes = getChangesAfterReset();
      break;

    // input key down handling
    case stateChangeTypes.KeyDownArrowDown:
      changes = {
        highlightedIndex: getNextWrappingIndex({
          moveAmount: 1,
          baseIndex: state.highlightedIndex,
          itemsLength: action.items.length,
        }),
      };
      break;

    // different key down handling
    case stateChangeTypes.KeyDownArrowUp:
      changes = {
        highlightedIndex: getNextWrappingIndex({
          moveAmount: -1,
          baseIndex: state.highlightedIndex,
          itemsLength: action.items.length,
        }),
      };
      break;

    case stateChangeTypes.KeyDownEnter:
      const someItemIsHighlighted = state.highlightedIndex >= 0;
      const defaultInputValue = getDefaultValue(props, 'inputValue');
      const defaultHighlightedIndex = getDefaultValue(props, 'highlightedIndex');
      const defaultSelectedItem = getDefaultValue(props, 'selectedItem');
      let inputValue = '';
      let isOpen = false;
      let highlightedIndex = defaultHighlightedIndex;
      let selectedItem;

      if (!someItemIsHighlighted) {
        isOpen = true;
        // 🐨 make a utility function akin to getDefaultValue and getInitValue called 'getStateOrDefault'
        highlightedIndex = state.highlightedIndex
          ? state.highlightedIndex
          : defaultHighlightedIndex;
        selectedItem = state.selectedItem ? state.selectedItem : defaultSelectedItem;

        if (action.inputField) {
          inputValue = action.inputFieldValue;
        } else {
          inputValue = selectedItem ? props.itemToString(selectedItem) : defaultInputValue;
        }
        inputValue = action.inputFieldValue ? action.inputFieldValue : defaultInputValue;
      } else {
        isOpen = getDefaultValue(props, 'isOpen');
        highlightedIndex = defaultHighlightedIndex;
        selectedItem = action.items[state.highlightedIndex];
        inputValue = props.itemToString(selectedItem);
      }

      changes = {
        isOpen,
        highlightedIndex,
        selectedItem,
        inputValue,
      };
      break;

    case stateChangeTypes.KeyDownEscape:
      {
        const selectedItem = state.selectedItem
          ? state.selectedItem
          : getDefaultValue(props, 'selectedItem');

        const inputValue = state.selectedItem
          ? props.itemToString(state.selectedItem)
          : getDefaultValue(props, 'inputValue');

        changes = {
          isOpen: false,
          highlightedIndex: -1,
          selectedItem,
          inputValue,
        };
      }

      break;

    case stateChangeTypes.ItemClick:
      {
        const selectedItem = action.items[action.index];
        changes = {
          isOpen: getDefaultValue(props, 'isOpen'),
          highlightedIndex: getDefaultValue(props, 'highlightedIndex'),
          selectedItem,
          inputValue: props.itemToString(selectedItem),
        };
      }
      break;
    case stateChangeTypes.ItemMouseMove:
      changes = {
        highlightedIndex: action.index,
      };
      break;
    case stateChangeTypes.ClearSelection:
      changes = {
        isOpen: getDefaultValue(props, 'isOpen'),
        highlightedIndex: getDefaultValue(props, 'highlightedIndex'),
        selectedItem: null,
        inputValue: '',
      };
      break;

    /** MENU STATE HANDLING */
    case stateChangeTypes.MenuOpen:
      changes = {
        isOpen: true,
        // 🐨 see if we need to do anything at all for highlightedIndex
        // highlightedIndex: getHighlightedIndexOnOpen(props, state, 0)
      };
      break;
    case stateChangeTypes.MenuClose:
      changes = {
        isOpen: false,
        // 🐨 see highlightedIndex thing
      };
      break;
    case stateChangeTypes.MenuToggle:
      changes = {
        isOpen: !state.isOpen,
        // 🐨 see highlightedIndex thing
      };
      break;

    /** ITEM SETTING HANDLING */
    case stateChangeTypes.MenuItemSelect:
      changes = getChangesAfterItemSelection();
      break;

    case stateChangeTypes.MenuItemSelectAtIndex:
      // 🐨 copied from downshift.js, see when this might be possible
      if (action.itemAtToSelectIndex === null) return;
      changes = getChangesAfterItemSelection();
      break;

    case stateChangeTypes.MenuItemSelectAtHighlightedIndex:
      changes = getChangesAfterItemSelection(state.highlightedIndex);
      break;

    /** SETTING HIGHLIGHT HANDLING */
    case stateChangeTypes.SetHightlightedIndex:
      changes = {
        highlightedIndex: action.highlightedIndex,
      };
      break;

    default:
      throw new Error('Reducer called without proper action type.');
  }

  return {
    ...state,
    ...changes,
  };
}

export function useEnhancedReducer(reducer, initialState, props) {
  // so that re renders do not see callback as new ( since there is only one reducer function )
  const enhancedReducer = useCallback(
    (state, action) => {
      state = getState(state, action.props);

      const {stateReducer} = action.props;
      const changes = reducer(state, action);
      const newState = stateReducer(state, {...action, changes});

      callOnChangeProps(action.props, state, newState);

      return newState;
    },
    [reducer],
  );

  const [state, dispatch] = useReducer(enhancedReducer, initialState);

  return [getState(state, props), dispatch];
}
